import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getUnassignedReturnsService } from '../../../../services/unassignedReturns.service';
import {
  buildDraftDefaults,
  buildInitialApplied,
  buildListPayload,
  callUnassigned,
  formatDateRange,
  notifyError,
  toggleSortOrder,
  toRow,
} from './unassignedUtils';

// `draft` is what the filter bar edits; `applied` drives the fetch and only changes on an explicit action.
export const useUnassignedFilters = (reasonOptions) => {
  const defaults = useMemo(buildDraftDefaults, []);
  const [draft, setDraft] = useState(defaults);
  const [applied, setApplied] = useState(() => buildInitialApplied(defaults));

  const handleApply = () => setApplied((prev) => ({ ...prev, ...draft, pageIndex: 1 }));

  const handleClear = () => {
    const fresh = buildDraftDefaults();
    setDraft(fresh);
    setApplied((prev) => ({ ...prev, ...fresh, pageIndex: 1, sortColumn: '', sortOrder: 'DESCENDING' }));
  };

  const handleSearch = (e, type) => {
    const isClear = type === 'clear';
    if (isClear) setDraft((prev) => ({ ...prev, searchKeywords: '' }));
    setApplied((prev) => ({ ...prev, searchKeywords: isClear ? '' : draft.searchKeywords, pageIndex: 1 }));
  };

  const handleCardClick = (code) => {
    const reasons = code === 'all' ? [] : reasonOptions.filter((o) => o.value === code);
    setDraft((prev) => ({ ...prev, reasons }));
    setApplied((prev) => ({ ...prev, reasons, pageIndex: 1 }));
  };

  const handleSubTab = (action) => setApplied((prev) => ({ ...prev, action, pageIndex: 1 }));

  const handleSort = (sortColumn) => setApplied((prev) => ({
    ...prev,
    sortColumn,
    sortOrder: toggleSortOrder(prev.sortOrder),
    pageIndex: 1,
  }));

  const setPageIndex = (pageIndex) => setApplied((prev) => ({ ...prev, pageIndex }));
  const setPageSize = (pageSize) => setApplied((prev) => ({ ...prev, pageSize, pageIndex: 1 }));

  return {
    draft, setDraft, applied,
    handleApply, handleClear, handleSearch, handleCardClick, handleSubTab, handleSort, setPageIndex, setPageSize,
  };
};

export const useUnassignedFetch = (applied, onRefreshSummary) => {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [subTabCounts, setSubTabCounts] = useState({ automaticCount: 0, manualCount: 0 });
  const [status, setStatus] = useState('IDLE');
  const requestId = useRef(0);

  const dateRange = useMemo(
    () => formatDateRange(applied.fromDate, applied.toDate),
    [applied.fromDate, applied.toDate]
  );

  const fetchList = useCallback(async () => {
    const current = ++requestId.current;
    setStatus('PENDING');
    const { ok, body, message } = await callUnassigned(
      () => getUnassignedReturnsService(buildListPayload(applied, dateRange)),
      'Failed to load unassigned returns'
    );
    if (current !== requestId.current) return;
    if (ok) {
      setRows((body.EntityList || []).map(toRow));
      setTotalRecords(body.TotalRecords || 0);
      setSubTabCounts({
        automaticCount: body.Metadata?.automaticCount ?? 0,
        manualCount: body.Metadata?.manualCount ?? 0,
      });
      setStatus('SUCCESS');
      return;
    }
    setRows([]);
    setTotalRecords(0);
    setStatus('FAILURE');
    notifyError(message);
  }, [applied, dateRange]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  useEffect(() => {
    onRefreshSummary(dateRange.fromDate, dateRange.toDate);
  }, [dateRange.fromDate, dateRange.toDate]);

  const refreshAll = useCallback(() => {
    void fetchList();
    onRefreshSummary(dateRange.fromDate, dateRange.toDate);
  }, [fetchList, onRefreshSummary, dateRange]);

  return { rows, setRows, totalRecords, subTabCounts, status, refreshAll };
};

export const useRowSelection = (rows, setRows) => {
  const selectedRows = useMemo(() => rows.filter((r) => r.isChecked), [rows]);

  // The table header checkbox passes an empty record.
  const handleCheckRecord = (e, record) => {
    const { checked } = e.target;
    const isSelectAll = record?.listIndex === undefined;
    setRows((prev) => prev.map((r) => (
      isSelectAll || r.listIndex === record.listIndex ? { ...r, isChecked: checked } : r
    )));
  };

  return { selectedRows, handleCheckRecord };
};
